import type { ScrollToOptions, Virtualizer } from '@tanstack/react-virtual';
import { memo } from '@tanstack/table-core';

import { isProduction } from '../../../../core/utils/is-node-environment.js';
import type { Table, TableFeature } from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableV2RowData } from '../../public.api.js';

const defaultScrollToOptions: ScrollToOptions = {
  align: 'start',
  behavior: 'auto',
};

/**
 * @internal
 */
export interface DataTableV2VirtualScrollingInstance {
  /**
   * Remeasure for row virtualization.
   */
  remeasureRow: (virtualizationContainerWidth?: number) => void;

  /**
   * Remeasure for column virtualization.
   */
  remeasureColumn: (columnId?: string | string[]) => void;

  registerRowVirtualizer: (
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
    columnVirtualizer: Virtualizer<HTMLDivElement, Element>,
  ) => void;

  /**
   * Gets the table's virtualization container, e.g. for querying elements within the table scope.
   */
  getVirtualizationContainer: () => HTMLDivElement | undefined;

  scrollToRow: (rowId: string) => void;

  /**
   * Returns the current vertical scroll position.
   */
  getRowScrollOffset: () => number | undefined | null;

  /**
   * Scrolls the row virtualizer to the pixel offset provided.
   */
  scrollToRowOffset: (offset: number | undefined | null) => void;

  _hasColumnCellsRendered: (columnId: string) => boolean;
}

/**
 * Feature implementation for virtual scrolling.
 * @internal
 */
export const DataTableV2VirtualScrolling: TableFeature = {
  createTable<TData extends DataTableV2RowData>(table: Table<TData>): void {
    let _rowVirtualizer: Virtualizer<HTMLDivElement, Element> | undefined =
      undefined;
    let _columnVirtualizer: Virtualizer<HTMLDivElement, Element> | undefined =
      undefined;

    // Register the `rowVirtualizer` and `columnVirtualizer`, reason for this approach is
    // that it is created via a react hook in a nested component but
    // we need access to it in `DataTableV2.tsx` => `useDataTableV2ConfigRef`
    table.registerRowVirtualizer = (
      rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
      columnVirtualizer: Virtualizer<HTMLDivElement, Element>,
    ) => {
      _rowVirtualizer = rowVirtualizer;
      _columnVirtualizer = columnVirtualizer;
    };

    table.getVirtualizationContainer = () => {
      return _rowVirtualizer?.scrollElement ?? undefined;
    };

    table.getRowScrollOffset = () => _rowVirtualizer?.scrollOffset;

    table.scrollToRowOffset = (offset: number | undefined | null) => {
      if (offset !== undefined && offset !== null) {
        _rowVirtualizer?.scrollToOffset(offset, {
          behavior: 'auto',
          align: 'start',
        });
      }
    };

    // Scroll to a given `rowId` using `rowVirtualizer.scrollToIndex`
    table.scrollToRow = (rowId: string) => {
      if (!_rowVirtualizer) {
        if (!isProduction()) {
          // eslint-disable-next-line no-console
          console.warn(
            '[Table - VirtualScrolling] No rowVirtualizer was registered',
          );
        }
        return;
      }

      // This code works because `allRows` will contain (only!) expanded subRows on the top level
      // When scrolling to a subRow the row has to be expanded beforehand.
      const allRows = table.getRowModel().rows;
      const rowIndex = allRows.findIndex((row) => row.id === rowId);

      if (rowIndex === -1) {
        if (!isProduction()) {
          // eslint-disable-next-line no-console
          console.warn(
            `[Table - VirtualScrolling] Row with id: "${rowId}" not found`,
          );
        }
        return;
      }

      _rowVirtualizer.scrollToIndex(rowIndex, defaultScrollToOptions);
    };

    function measureRows() {
      // Early exit if the rowVirtualizer or scrollElement are not present
      if (
        _rowVirtualizer === undefined ||
        _rowVirtualizer.scrollElement === null
      ) {
        return;
      }

      // Select the grid defining container.
      const gridContainer = _rowVirtualizer.scrollElement.querySelector(
        '[style*="grid-template-rows"]',
      );

      // Early exit if gridContainer is not a HTMLElement
      if (gridContainer === null || !(gridContainer instanceof HTMLElement)) {
        return;
      }

      // Store the original grid row template so it can be applied afterwards.
      const originalGridTemplate = gridContainer.style.gridTemplateRows;
      try {
        let templateRows = table.getHeaderGridTemplateRows();
        const rowCount = _rowVirtualizer.options.count;

        if (rowCount > 0) {
          // Set the row template for body rows to min-content so all rows can be measured by their actual size.
          const bodyRows = `repeat(${rowCount}, min-content)`;
          templateRows = `${templateRows} ${bodyRows} [body-end]`;
        }

        gridContainer.style.gridTemplateRows = templateRows;

        // Select all row elements with the data attribute 'data-row'
        const measureRows =
          _rowVirtualizer.scrollElement.querySelectorAll('[data-row]');

        // Iterate over each measurements cell
        for (const measureRow of Array.from(measureRows)) {
          // Ensure the element is an instance of HTMLElement
          if (!(measureRow instanceof HTMLElement)) {
            continue;
          }
          _rowVirtualizer.measureElement(measureRow);
        }

        const virtualItems = _rowVirtualizer.getVirtualItems();
        const virtualItemsLength = virtualItems.length;

        const firstMeasuredRow =
          measureRows.item(0)?.getAttribute('data-row') ?? null;
        const isDifferentRowsVisible =
          firstMeasuredRow !== null &&
          virtualItemsLength > 0 &&
          Number(firstMeasuredRow) !== virtualItems[0].index;

        // If different rows are measured than those that are currently visible or
        // if fewer items are measured than would now fit into the scrollContainer
        // we reset the measurements cache and allow it to remeasure all items again.
        if (isDifferentRowsVisible || measureRows.length < virtualItemsLength) {
          _rowVirtualizer.measure();
        }
      } finally {
        // Restore the original grid template.
        gridContainer.style.gridTemplateRows = originalGridTemplate;
      }
    }

    table.remeasureRow = memo(
      (containerWidth) => {
        const expandingState = table.options.enableExpanding
          ? Object.keys(table.getState().expanded).length !== 0 &&
            table.getState().expanded
          : undefined;
        const rowDetailsState = table.options.enableRowDetails
          ? table.getState().rowDetails
          : undefined;
        const sortingState = table.options.enableSorting
          ? table.getState().sorting
          : undefined;
        return [
          containerWidth,
          expandingState,
          rowDetailsState,
          sortingState,
          table.getState().columnLineWrap,
          table.getState().visualVariantConfig.rowDensity,
          table.getState().visualVariantConfig.rowSeparation,
          table.getState().columnSizing,
          table.getState().pagination.pageIndex,
          table.getState().pagination.pageSize,
          table.getState().columnVisibility,
          table.getState().rowOrder,
        ];
      },
      (containerWidth) => {
        if (containerWidth === 0) {
          return;
        }

        // Need to shift this into the next update cycle in order to make sure that
        // all styles that could change are actually already updated on the table.
        // Without the shift, lineWrap values might not be updated yet when measuring.
        table._queue(measureRows);
      },
      getMemoOptions(table.options, 'debugTable', 'table.remeasureRow'),
    );

    function measureColumn(columnIdOrIds: string | string[]) {
      if (
        _columnVirtualizer === undefined ||
        _columnVirtualizer.scrollElement === null
      ) {
        return;
      }

      // Select the grid defining container.
      const gridContainer = _columnVirtualizer.scrollElement.querySelector(
        '[style*="grid-template-columns"]',
      );
      if (gridContainer === null || !(gridContainer instanceof HTMLElement)) {
        return;
      }

      // Store the original grid row template so it can be applied afterwards.
      const originalGridTemplate = gridContainer.style.gridTemplateColumns;
      try {
        const columnIds = Array.isArray(columnIdOrIds)
          ? columnIdOrIds
          : [columnIdOrIds];
        const overrides: Record<
          string,
          { width: undefined; height: undefined }
        > = {};
        for (const singleColumnId of columnIds) {
          overrides[singleColumnId] = { width: undefined, height: undefined };
        }

        gridContainer.style.gridTemplateColumns =
          table.getTableGridTemplate(overrides);
        for (const columnId of columnIds) {
          // Select all cells with the data attribute 'data-columnid'
          const measureCell = _columnVirtualizer.scrollElement.querySelector(
            `[role="cell"][data-columnid="${CSS.escape(columnId)}"]`,
          );

          // Ensure the element is an instance of HTMLElement
          if (!(measureCell instanceof HTMLElement)) {
            return;
          }

          // Parse the row index from the data attribute
          const index = parseInt(measureCell.dataset.column ?? '');

          // Check if the index is a valid number
          if (!Number.isNaN(index)) {
            // Measure the new height of the element
            const { width, height } = measureCell.getBoundingClientRect();
            // Update the virtualizer with the new width
            table._setHeaderSize(columnId, {
              width,
              height,
            });
          }
        }
      } finally {
        // Restore the original grid template.
        gridContainer.style.gridTemplateColumns = originalGridTemplate;
      }
    }

    table.remeasureColumn = memo(
      (columnIdsOrId?: string | string[]) => {
        return [columnIdsOrId, table.options.columns];
      },
      (columnIdsOrId?: string | string[]) => {
        if (columnIdsOrId === undefined) {
          return null;
        }

        table._queue(() => measureColumn(columnIdsOrId));
      },
      getMemoOptions(table.options, 'debugTable', 'table.remeasureRow'),
    );

    table._hasColumnCellsRendered = (columnId: string) => {
      if (
        _columnVirtualizer === undefined ||
        _columnVirtualizer.scrollElement === null
      ) {
        return false;
      }

      return (
        _columnVirtualizer?.scrollElement.querySelector(
          `[role="cell"][data-columnid="${CSS.escape(columnId)}"]`,
        ) !== null
      );
    };
  },
};
